import { create } from 'zustand';
import { authApi } from '@/api/auth';
import { storage, StorageKeys } from '@/utils/storage';
import { useCartStore } from '@/stores/cartStore';
import type { AuthResponse, CurrentUser, LoginRequest, RoleName } from '@/types';

interface AuthState {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  loading: boolean;

  /** On app boot, hydrate from storage (synchronous — no network). */
  hydrate: () => void;

  login: (req: LoginRequest) => Promise<void>;
  logout: () => void;

  /** Does the current user have any of these roles? */
  hasAnyRole: (roles: RoleName[]) => boolean;
}

function userFromAuth(a: AuthResponse): CurrentUser {
  return {
    userId: a.userId,
    username: a.username,
    email: a.email,
    tenantId: a.tenantId,
    tenantCode: a.tenantCode,
    tenantName: a.tenantName,
    tenantType: a.tenantType,
    industry: a.industry,
    roles: a.roles,
  };
}

function readStoredAuth(): { user: CurrentUser | null; isAuthenticated: boolean } {
  const raw = storage.get(StorageKeys.CURRENT_USER);
  const token = storage.get(StorageKeys.ACCESS_TOKEN);
  if (raw && token) {
    try {
      return { user: JSON.parse(raw) as CurrentUser, isAuthenticated: true };
    } catch {
      storage.clear();
    }
  }
  return { user: null, isAuthenticated: false };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ...readStoredAuth(),
  loading: false,

  // kept for backwards-compat — store is already hydrated at creation time
  hydrate: () => {},

  login: async (req) => {
    set({ loading: true });
    // Clear previous user's cart before loading the new user's data
    useCartStore.getState().clear();
    try {
      const auth = await authApi.login(req);
      storage.set(StorageKeys.ACCESS_TOKEN, auth.accessToken);
      storage.set(StorageKeys.REFRESH_TOKEN, auth.refreshToken);
      const user = userFromAuth(auth);
      storage.set(StorageKeys.CURRENT_USER, JSON.stringify(user));
      set({ user, isAuthenticated: true });
    } finally {
      set({ loading: false });
    }
  },

  logout: () => {
    useCartStore.getState().clear();
    storage.clear();
    set({ user: null, isAuthenticated: false });
  },

  hasAnyRole: (roles) => {
    const u = get().user;
    if (!u) return false;
    return roles.some((r) => u.roles.includes(r));
  },
}));
